/**
 * Bug-report capture buffers — the rolling context the in-app 🐞 reporter attaches.
 *
 * Three small ring buffers (last 10 each), filled as the user moves around:
 *   - page_history    ← recorded on every route change (usePageTracking)
 *   - request_history ← recorded by the axios response interceptor (api/client.ts)
 *   - console_errors  ← recorded by installConsoleCapture() (console.error + window error events)
 *
 * Pure leaf module: no React, no app imports (so api/client.ts can import it without a cycle).
 * Everything is best-effort and swallows its own errors — capture must never break the app.
 */
const MAX = 10;

interface PageEntry {
  path: string;
  at: string;
}
interface RequestEntry {
  method: string;
  path: string;
  status: number | string;
  at: string;
}
interface ErrorEntry {
  message: string;
  at: string;
}

const pages: PageEntry[] = [];
const requests: RequestEntry[] = [];
const errors: ErrorEntry[] = [];

function push<T>(buf: T[], item: T): void {
  buf.push(item);
  if (buf.length > MAX) buf.shift();
}

function now(): string {
  try {
    return new Date().toISOString();
  } catch {
    return "";
  }
}

export function recordPage(path: string): void {
  push(pages, { path, at: now() });
}

export function recordRequest(method: string, path: string, status: number | string): void {
  push(requests, { method: (method || "GET").toUpperCase(), path: path || "", status, at: now() });
}

export function recordConsoleError(message: unknown): void {
  push(errors, { message: String(message).slice(0, 1000), at: now() });
}

/** The snapshot the reporter sends in `state_snapshot`. */
export function getCapture(): {
  page_history: PageEntry[];
  request_history: RequestEntry[];
  console_errors: ErrorEntry[];
} {
  return {
    page_history: [...pages],
    request_history: [...requests],
    console_errors: [...errors],
  };
}

let installed = false;
/** Patch console.error + window error/rejection events to feed the console_errors buffer. Idempotent. */
export function installConsoleCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  const orig = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      recordConsoleError(args.map((a) => (a instanceof Error ? a.message : a)).join(" "));
    } catch {
      /* never break logging */
    }
    orig(...args);
  };
  window.addEventListener("error", (e) => recordConsoleError(e.message || String(e.error)));
  window.addEventListener("unhandledrejection", (e) =>
    recordConsoleError("unhandledrejection: " + String((e as PromiseRejectionEvent).reason)),
  );
}
